const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const UTM_STORAGE_KEY = 'utm_data';
const MAX_UTM_LENGTH = 100;

/**
 * Capture UTM data from all available sources and persist.
 * MUST be called before any OAuth redirect.
 * All operations are synchronous (localStorage + cookie) - no network calls.
 */
export function captureAndPersistUtm(): Record<string, string> | null {
  // 1. Query params (highest priority - freshest data)
  const params = new URLSearchParams(window.location.search);
  const utmFromUrl: Record<string, string> = {};
  for (const key of UTM_PARAMS) {
    const value = params.get(key)?.trim().slice(0, MAX_UTM_LENGTH);
    if (value) utmFromUrl[key] = value;
  }
  if (Object.keys(utmFromUrl).length > 0) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmFromUrl));
    return utmFromUrl;
  }

  // 2. Cookie (cross-domain from marketing site)
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('utm_data='));
  if (cookie) {
    try {
      const value = JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('=')));
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(value));
      return value;
    } catch { /* ignore malformed cookie */ }
  }

  // 3. localStorage fallback (survives OAuth redirect)
  const stored = localStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try { return JSON.parse(stored); } catch { /* ignore */ }
  }

  return null;
}

/**
 * Get persisted UTM data (post-OAuth, use localStorage as primary).
 */
export function getPersistedUtm(): Record<string, string> | null {
  const stored = localStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try { return JSON.parse(stored); } catch { /* ignore */ }
  }

  // Fallback to cookie
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('utm_data='));
  if (cookie) {
    try {
      return JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('=')));
    } catch { /* ignore */ }
  }

  return null;
}

export function clearUtmData(): void {
  localStorage.removeItem(UTM_STORAGE_KEY);

  // The marketing site sets the cookie with Domain=.optiedge.cc or Domain=.optidev.ai.
  // To clear it, the Domain must match exactly how it was set.
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const parts = window.location.hostname.split('.');
  const parentDomain = parts.slice(-2).join('.');

  document.cookie = `utm_data=; ${expiry}; path=/; domain=.${parentDomain}`;
  document.cookie = `utm_data=; ${expiry}; path=/`;
}
